import { EventListItemSkeleton, GenericTextSkeleton } from "@hooplab/ui-components";
import { format, getYear } from "date-fns";
import { ReactComponent as IconSearch } from "../../assets/icons/search.svg";
import React from "react";
import { FormattedMessage } from "react-intl";
import BackendRequest from "../../components/BackendRequest";
import EventListItemView from "../../components/EventListItem";
import Heading from "../../components/Heading";
import { Box } from "@hooplab/ui-components";
import { fetchEvents } from "../../dao/events";
import colors from "../../styles/colors";
import { EventListItem } from "../../types";
import ErrorPage from "../ErrorPage";
import * as styles from "./styles";
import * as storage from "../../helpers/storage";
import { getLocale } from "../../helpers/prettyDate";

const getEventGroupKey = (d: Date) => {
  const now = new Date();
  if (now > d) {
    return "Ongoing";
  } else {
    return `${getYear(d)}-${format(d, "MM")}`;
  }
};

const groupEvents = (events: EventListItem[]) =>
  events.reduce((acc, event) => {
    const key = getEventGroupKey(event.start);
    if (acc[key]) {
      acc[key].push(event);
    } else {
      acc[key] = [event];
    }

    return acc;
  }, {} as { [k: string]: EventListItem[] });

const getFilteredEvents = (events: EventListItem[], query: string, showHiddenEvents: boolean) => {
  const q = query.toLowerCase();
  if (showHiddenEvents) {
    return events.filter((e) => e.title.toLowerCase().indexOf(q) >= 0);
  }
  return events.filter((e) => e.title.toLowerCase().indexOf(q) >= 0 && e.saleState === "active");
};

export const EventListSkeleton = () => (
  <div className={styles.container}>
    <div className={styles.list}>
      <div className={styles.listHeading}>
        <GenericTextSkeleton fontSize="1.25rem" />
      </div>
      {Array.apply(null, Array(5)).map((v, i) => (
        <div key={i} className={styles.listItem}>
          <EventListItemSkeleton />
        </div>
      ))}
    </div>
  </div>
);

const EventList: React.FC<{ organizationName: string }> = (props) => {
  const [query, setQuery] = React.useState("");
  const [showHiddenEvents] = React.useState(false);
  const nowKey = getEventGroupKey(new Date());
  const lang = storage.getItem("lang", "nb-NO");

  const renderGroupHeader = (key: string, event: EventListItem) => {
    if (key === nowKey) {
      return (
        <FormattedMessage
          id={"screens.OrganizationEventList.EventList.thisMonth"}
          defaultMessage={"This month"}
        />
      );
    } else if (key === "Ongoing") {
      return (
        <FormattedMessage
          id={"screens.OrganizationEventList.EventList.ongoing"}
          defaultMessage={"Ongoing"}
        />
      );
    }
    const formatted = format(event.start, "MMMM yyyy", { locale: getLocale(lang) });
    return (
      <React.Fragment>{formatted.charAt(0).toUpperCase() + formatted.slice(1)}</React.Fragment>
    );
  };

  return (
    <BackendRequest requestFn={fetchEvents}>
      {{
        loading: () => <EventListSkeleton />,
        error: (error) => <ErrorPage />,
        success: (events) => {
          const eventGroups = groupEvents(getFilteredEvents(events, query, showHiddenEvents));

          if (events.length === 0) {
            return (
              <div className={styles.noEventsContainer}>
                <FormattedMessage
                  id={"screens.OrganizationEventList.EventList.noFutureEvents"}
                  defaultMessage={"No future events"}
                />
              </div>
            );
          }

          return (
            <div className={styles.container}>
              {events.length >= 2 && (
                <Box additionalCls={styles.inputWrapper}>
                  <IconSearch width={18} height={18} color="#B6B6B6" className={styles.inputIcon} />
                  <input
                    className={styles.input}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={`Finn noe som skjer på ${props.organizationName}`}
                  />
                </Box>
              )}
              {Object.keys(eventGroups).length === 0 && query.length > 0 && (
                <div className={styles.noEventsContainer}>
                  <FormattedMessage
                    id={"screens.OrganizationEventList.EventList.noEventsFound"}
                    defaultMessage={"We could not find any events with the search query '{query}'"}
                    values={{ query: query }}
                  />
                </div>
              )}

              {Object.keys(eventGroups)
                .sort((a, b) => {
                  if (a === "Ongoing") {
                    return -1;
                  } else if (b === "Ongoing") {
                    return 1;
                  } else {
                    return a.localeCompare(b);
                  }
                })
                .map((yearAndMonth) => {
                  const events = eventGroups[yearAndMonth];

                  return (
                    <div className={styles.list} key={yearAndMonth}>
                      <div className={styles.listHeading}>
                        <Heading color={colors.grey[500]} size="xl">
                          {renderGroupHeader(yearAndMonth, events[0])}
                        </Heading>
                      </div>
                      {events.map((event) => (
                        <div key={event.eventId} className={styles.listItem}>
                          <EventListItemView {...event} />
                        </div>
                      ))}
                    </div>
                  );
                })}
            </div>
          );
        },
      }}
    </BackendRequest>
  );
};

export default EventList;
